import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SurveyScoreMetric } from '@/types/dashboard.types';
import { DateRange } from '@/types/dashboard.types';
import { BarChart } from './bar-chart';
import {
  getChangeColorClass,
  getChangeLabel,
  getChartLabel,
  getValueSublabel,
} from './analytics-card-helpers';

interface RouteSurveyScoreCardProps {
  title: string;
  metric: SurveyScoreMetric;
  dateRange: DateRange;
  onNavigate: () => void;
  tooltip?: string;
}

export function RouteSurveyScoreCard({
  title,
  metric,
  dateRange,
  onNavigate,
  tooltip,
}: RouteSurveyScoreCardProps) {
  const trendline = metric.trendline || [];
  const periodLabel = metric.trendlinePeriodLabel;

  const hasChange = metric.absoluteChange !== undefined;
  const isIncrease = metric.isIncrease ?? false;
  const isZeroChange = metric.absoluteChange === 0;
  const changeColor = getChangeColorClass(isIncrease, isZeroChange);

  const icon = tooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="analytics-card-external-link" onClick={onNavigate}>
            <ExternalLink />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <div className="analytics-card-external-link" onClick={onNavigate}>
      <ExternalLink />
    </div>
  );

  const cardContent = (
    <Card className="analytics-card">
      <CardContent className="analytics-card-content">
        {icon}
        <div className="analytics-card-title">{title}</div>
        <div className="analytics-card-value-container">
          <span className="analytics-card-value">{metric.value}</span>
          {hasChange && (
            <div className={`analytics-card-change-row ${changeColor}`}>
              <span className="analytics-card-change-value">
                {isZeroChange || isIncrease ? '↑' : '↓'} {metric.absoluteChange}
              </span>
              <span className="analytics-card-change-label">
                {getChangeLabel(dateRange)}
              </span>
            </div>
          )}
        </div>
        <div className="analytics-card-sublabel">
          {getValueSublabel(dateRange, 'score')}
        </div>
        <BarChart
          trendline={trendline}
          label={getChartLabel(dateRange, periodLabel, trendline, 'scores')}
          scaleType="score"
          dateRange={dateRange}
          periodLabel={periodLabel}
          averageValue={metric.value}
        />
      </CardContent>
    </Card>
  );

  return cardContent;
}
